import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import IronSightMark from '../../shared/IronSightMark';
import { formatCurrency, formatNumber } from '../../../utils/formatting';
import { aiPriceGapDisplay } from '../../../utils/aiPrice';
import { grossMarginGood } from '../../../utils/unitIntelligence';
import { useDealershipSettings } from '../../../contexts/settingsContext';
import {
    fmtHistDate, fmtPricePct, priceChangeTone, priceHistoryPoints,
} from '../../../utils/unitPricing';
import './IronSightCards.css';

// Increase green, reduction orange, flat muted (utils/unitPricing tones).
const TONE_COLORS = { up: '#15803d', down: '#b45309', flat: '#718096' };
const phChangeColor = (pct) => TONE_COLORS[priceChangeTone(pct)];
const fmtPct = fmtPricePct;

// ── Sparkline ──────────────────────────────────────────────────────────────
// Hand-rolled inline SVG (no chart lib). Width is measured so the SVG renders
// at 1:1 pixel scale — the line stays a crisp 2px and the change dots stay
// perfectly round regardless of the card width.
function PriceSparkline({ unit, history }) {
    const wrapRef = useRef(null);
    const [width, setWidth] = useState(0);

    useEffect(() => {
        const el = wrapRef.current;
        if (!el) return undefined;
        const measure = () => setWidth(el.clientWidth);
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const H = 60;
    const padX = 5;
    const padTop = 12;
    const padBottom = 12;

    const now = Date.now();
    // Series: original price at listing date, each change's new price at its
    // date, then the current price carried flat to today (the emphasised dot).
    const pts = priceHistoryPoints(unit, history, now);

    const tMin = pts[0].t;
    const tMax = Math.max(now, pts[pts.length - 1].t);
    const prices = pts.map((p) => p.price);
    const pMin = Math.min(...prices);
    const pMax = Math.max(...prices);

    const xOf = (t) => (tMax === tMin ? padX : padX + ((t - tMin) / (tMax - tMin)) * (width - 2 * padX));
    const yOf = (p) => (pMax === pMin ? H / 2 : padTop + ((pMax - p) / (pMax - pMin)) * (H - padTop - padBottom));

    const coords = pts.map((p) => ({ ...p, x: xOf(p.t), y: yOf(p.price) }));
    const line = coords
        .map((c, i) => `${i === 0 ? 'M' : 'L'} ${c.x.toFixed(1)} ${c.y.toFixed(1)}`)
        .join(' ');
    const baseline = (H - padBottom + 5).toFixed(1);
    const area = `${line} L ${coords[coords.length - 1].x.toFixed(1)} ${baseline} L ${coords[0].x.toFixed(1)} ${baseline} Z`;

    const stroke = phChangeColor(history.total_change_pct);
    const gradId = `ph-grad-${history.stock_number}`;
    const endPt = coords[coords.length - 1];

    return (
        <div className="ph-spark-wrap" ref={wrapRef}>
            {width > 0 && (
                <svg
                    className="ph-spark"
                    width={width}
                    height={H}
                    viewBox={`0 0 ${width} ${H}`}
                    role="img"
                    aria-label="Asking price over time"
                >
                    <defs>
                        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
                            <stop offset="0%" stopColor={stroke} stopOpacity="0.18" />
                            <stop offset="100%" stopColor={stroke} stopOpacity="0" />
                        </linearGradient>
                    </defs>
                    <path d={area} fill={`url(#${gradId})`} stroke="none" />
                    <path
                        d={line}
                        fill="none"
                        stroke={stroke}
                        strokeWidth="2"
                        strokeLinejoin="round"
                        strokeLinecap="round"
                    />
                    {coords.map((c, i) => {
                        if (c.kind === 'current') return null;
                        if (c.kind === 'origin') {
                            return (
                                <circle
                                    key={i}
                                    cx={c.x}
                                    cy={c.y}
                                    r="2.5"
                                    fill="#ffffff"
                                    stroke={stroke}
                                    strokeWidth="1.5"
                                />
                            );
                        }
                        return <circle key={i} cx={c.x} cy={c.y} r="2.5" fill={stroke} />;
                    })}
                    <circle cx={endPt.x} cy={endPt.y} r="4" fill={stroke} stroke="#ffffff" strokeWidth="1.5" />
                </svg>
            )}
        </div>
    );
}

PriceSparkline.propTypes = {
    unit: PropTypes.object,
    history: PropTypes.object.isRequired,
};

// ── Price History card ──────────────────────────────────────────────────────
function PriceHistorySection({ unit, priceHistory, loading, error }) {
    const [expanded, setExpanded] = useState(false);

    let body;
    if (loading) {
        body = (
            <div className="ph-skeleton" aria-label="Loading price history">
                <div className="ph-skel-chip" />
                <div className="ph-skel-spark" />
            </div>
        );
    } else if (error) {
        body = <p className="ph-error">{error}</p>;
    } else if (!priceHistory) {
        body = <p className="ph-error">Price history unavailable.</p>;
    } else if (!priceHistory.num_changes) {
        body = (
            <p className="ph-empty">
                No price changes — listed at{' '}
                <strong>{formatCurrency(priceHistory.current_price)}</strong> since{' '}
                {formatNumber(priceHistory.days_on_market)} days ago.
            </p>
        );
    } else {
        const { num_changes, total_change_pct, days_on_market, changes } = priceHistory;
        body = (
            <>
                <div className="ph-summary">
                    <span className="ph-chip">
                        {num_changes} price change{num_changes !== 1 ? 's' : ''}
                    </span>
                    <span className="ph-chip-sep">&middot;</span>
                    <span className="ph-chip-pct" style={{ color: phChangeColor(total_change_pct) }}>
                        {fmtPct(total_change_pct)}
                    </span>
                    <span className="ph-chip-sub">over {formatNumber(days_on_market)} days</span>
                </div>

                <PriceSparkline unit={unit} history={priceHistory} />

                <button
                    className="ph-toggle"
                    onClick={() => setExpanded((v) => !v)}
                    aria-expanded={expanded}
                >
                    <svg
                        className={`ph-chevron${expanded ? ' ph-chevron--open' : ''}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        width="12"
                        height="12"
                        aria-hidden="true"
                    >
                        <path
                            fillRule="evenodd"
                            d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                            clipRule="evenodd"
                        />
                    </svg>
                    {expanded ? 'Hide changes' : 'View all changes'}
                </button>

                {expanded && (
                    <div className="ph-table-wrap">
                        <table className="ph-table">
                            <thead>
                                <tr>
                                    <th>Date</th>
                                    <th>Old &rarr; New</th>
                                    <th>Change</th>
                                </tr>
                            </thead>
                            <tbody>
                                {[...changes].reverse().map((c, i) => (
                                    <tr key={`${c.date}-${i}`}>
                                        <td className="ph-nowrap">{fmtHistDate(c.date)}</td>
                                        <td className="ph-nowrap">
                                            {formatCurrency(c.old_price)}
                                            <span className="ph-arrow">&rarr;</span>
                                            {formatCurrency(c.new_price)}
                                        </td>
                                        <td
                                            className="ph-nowrap"
                                            style={{ color: phChangeColor(c.change_pct), fontWeight: 600 }}
                                        >
                                            {fmtPct(c.change_pct)}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </>
        );
    }

    return (
        <section className="detail-section is-card">
            <div className="is-card-head">
                <span className="is-card-eyebrow"><IronSightMark /> IronSight</span>
                <span className="is-card-title">Price History</span>
            </div>
            <div className="is-card-body">{body}</div>
        </section>
    );
}

PriceHistorySection.propTypes = {
    unit: PropTypes.object,
    priceHistory: PropTypes.object,
    loading: PropTypes.bool,
    error: PropTypes.string,
};

function PricingTab({ unit, priceHistory, priceHistoryLoading, priceHistoryError }) {
    const { gapThresholds, marginFloorPct } = useDealershipSettings();
    const aiPrice = unit.PredictedPrice || null;
    const askingPrice = unit.Best_Sell_Price;
    const hasPriceIntel = aiPrice && askingPrice;
    // Same scale, wording and colours as the inventory table's AI Price chip.
    const gapDisplay = hasPriceIntel ? aiPriceGapDisplay(unit, gapThresholds) : null;
    const priceGapLabel = gapDisplay?.label;
    const priceGapColor = gapDisplay?.color;

    return (
        <div className="tab-content">
            <section className="detail-section is-card">
                <div className="is-card-body">
                    <h3 className="is-section-title">Pricing</h3>
                    <div className="detail-grid">
                        <div className="detail-item">
                            <label>Asking Price</label>
                            <span className="price-value">{formatCurrency(unit.Best_Sell_Price)}</span>
                        </div>
                        <div className="detail-item">
                            <label>Book Cost</label>
                            <span>{formatCurrency(unit.Sales_Book_Value)}</span>
                        </div>
                        {unit.OVRALLOW != null && unit.OVRALLOW !== 0 && (
                            <div className="detail-item">
                                <label>Over Allowance</label>
                                <span>{formatCurrency(unit.OVRALLOW)}</span>
                            </div>
                        )}
                        <div className="detail-item">
                            <label>Net Cost</label>
                            <span className="internal-price">{formatCurrency(unit.InternalPrice)}</span>
                        </div>
                        {unit.GrossMargin != null && (
                            <div className="detail-item">
                                <label>Gross Margin</label>
                                <span style={{ color: grossMarginGood(unit.GrossMarginPct, marginFloorPct) ? '#15803d' : '#b45309', fontWeight: 600 }}>
                                    {formatCurrency(unit.GrossMargin)} ({unit.GrossMarginPct?.toFixed(1)}%)
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {hasPriceIntel && (
                <section className="detail-section is-card">
                    <div className="is-card-head">
                        <span className="is-card-eyebrow"><IronSightMark /> IronSight</span>
                        <span className="is-card-title">AI Price Comparison</span>
                    </div>
                    <div className="is-card-body">
                        <div className="ai-price-comparison">
                            <div className="comparison-bars">
                                <div className="comparison-bar-row">
                                    <span className="comparison-label">Asking</span>
                                    <div className="comparison-bar-track">
                                        <div
                                            className="comparison-bar-fill asking"
                                            style={{ width: `${Math.min(100, (askingPrice / Math.max(askingPrice, aiPrice)) * 100)}%` }}
                                        ></div>
                                    </div>
                                    <span className="comparison-value">{formatCurrency(askingPrice)}</span>
                                </div>
                                <div className="comparison-bar-row">
                                    <span className="comparison-label">AI Est.</span>
                                    <div className="comparison-bar-track">
                                        <div
                                            className="comparison-bar-fill ai"
                                            style={{ width: `${Math.min(100, (aiPrice / Math.max(askingPrice, aiPrice)) * 100)}%` }}
                                        ></div>
                                    </div>
                                    <span className="comparison-value">{formatCurrency(aiPrice)}</span>
                                </div>
                            </div>
                            <div className="comparison-gap" style={{ color: priceGapColor }}>
                                Gap: {gapDisplay?.pctText} — {priceGapLabel}
                            </div>
                        </div>
                    </div>
                </section>
            )}

            <PriceHistorySection
                unit={unit}
                priceHistory={priceHistory}
                loading={priceHistoryLoading}
                error={priceHistoryError}
            />

            <section className="detail-section is-card">
                <div className="is-card-body">
                    <h3 className="is-section-title">Usage &amp; Meters</h3>
                    <div className="detail-grid">
                        <div className="detail-item">
                            <label>Hours / Odometer</label>
                            <span>{formatNumber(unit.ODOMETER)}</span>
                        </div>
                        <div className="detail-item">
                            <label>Usage Type</label>
                            <span>{unit.Usage || 'N/A'}</span>
                        </div>
                        <div className="detail-item">
                            <label>Separator Meter</label>
                            <span>{unit.Sep_Meter ? formatNumber(unit.Sep_Meter) : 'N/A'}</span>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}

PricingTab.propTypes = {
    unit: PropTypes.object.isRequired,
    priceHistory: PropTypes.object,
    priceHistoryLoading: PropTypes.bool,
    priceHistoryError: PropTypes.string,
};

PricingTab.defaultProps = {
    priceHistory: null,
    priceHistoryLoading: false,
    priceHistoryError: null,
};

export default PricingTab;
